/**
 * Serializes an array of objects into a JSON string, indexed by their position in the array.
 * If a key is provided, it will only include that key's value in the serialized object.
 * If multiple keys are provided, it will include those keys' values in the serialized object.
 *
 * @param data - The array of objects to serialize.
 * @param key - The key or keys to include in the serialized object.
 * @returns A JSON string representing the indexed objects.
 *
 * @example
 * // 1 key:
 * {"0": "provided_key_value", "1": "provided_key_value"}
 *
 * // 2 keys (key1, key2):
 * {"0": {"key1": "value1", "key2": "value2"}}
 *
 * // no key:
 * {"0": {"key1": "value1", "key2": "value2", "key3": "value3"}}
 */
export function getSerializedIndexedObject<T extends Record<string, any>, K extends keyof T>(
  data: T[],
  key?: K | K[],
): string {
  return JSON.stringify(
    data.reduce(
      (acc, item, i) => {
        if (!key) {
          acc[i] = item;
          return acc;
        }

        if (Array.isArray(key)) {
          const result = {} as Record<string, any>;
          key.forEach((k) => {
            result[k as string] = item[k];
          });
          acc[i] = result as Pick<T, K>;

          return acc;
        }

        acc[i] = item[key] as T[K];
        return acc;
      },
      {} as Record<number, any>,
    ),
  );
}
